/**
 * Frozen cache identities for `@heidi/patients` (D2.1, ARCFE-1013).
 *
 * Byte-identical to the literals the app already ships. A relocation that
 * changes a key splits the cache in two — the app writes under the old
 * identity, the package reads the new one — so these are copied verbatim and
 * the legacy sites become re-exports, never re-typed literals.
 */

/**
 * NOTE the literal: it is a template-looking string that is NOT interpolated
 * (`'/patient-summary-delivery/${source}/email-preview'` with `source` never
 * substituted). That is what the app ships, so it is what the identity is.
 * Making it a real template here would silently repartition the cache.
 */
export const GET_PATIENT_SUMMARY_EMAIL_PREVIEW =
  '/patient-summary-delivery/${source}/email-preview';

// ---------------------------------------------------------------------------
// Document + recap identities (D2.2, ARCFE-1020). Byte-identical to the app's.
// ---------------------------------------------------------------------------

export const GET_PATIENT_DOCUMENTS = 'get_patient_documents';

export const GET_PATIENT_RECAP = 'get_patient_recap';

export const GET_PATIENT_DOCUMENT_DOWNLOAD_URL = 'get_patient_document_download_url';

// `GET_SESSION_PATIENT_DOCUMENT_LINKS` LEFT THIS FILE on 2026-08-15
// (ARCFE-1076). It was declared here from ARCFE-1020 with the note that keys
// are single-site "regardless of which lane ends up owning the hook" — the
// registry itself recorded the ownership as undecided, and the open decision
// `session-patient-document-link-owner` is what held it. That decision is now
// RESOLVED: the program owner ruled the whole link cluster SESSION's on the
// resource-owner rule (`/sessions/{id}/patient-documents`), so the declaration
// moved verbatim to `@heidi/session/contracts` and the string is unchanged.
// It is NOT re-exported from here: nothing in this package or the patients
// lane reads it, and a re-export would create a patients -> session edge for
// zero consumers.
/** Patient-profile list identities (D2.2, ARCFE-1021). */
export const GET_PATIENT_PROFILES = 'get_patient_profiles';
export const GET_PATIENT_PROFILES_BY_EHR = 'get_patient_profiles_by_ehr';

/**
 * Patient-profile SEARCH identity (ARCFE-1057).
 *
 * The only key in this file whose declaration site was under `src/features/**`
 * rather than a hub — `src/features/patient-find-or-link/hooks/useSearchPatientProfiles.ts`
 * declared it inline, where no `legacy.hubs` pattern could reach it, so the
 * family ratchet measured the file as absent (ARCFE-1054's census found it and
 * recommended this carve). Moving the declaration here is what makes
 * `key-single-site` true for it: the old path re-exports the name and does not
 * re-type the literal.
 *
 * The identity is the literal AND the key's shape. Its queryKey is FOUR
 * elements — `[SEARCH_PATIENT_PROFILES, debouncedQuery, limit, sessionId]` —
 * and dropping or adding one repartitions the cache just as surely as editing
 * the string would, so `keys.test.ts` pins the arity next to the value.
 * `tracking` is deliberately NOT in the key (see the hook).
 */
export const SEARCH_PATIENT_PROFILES = 'search_patient_profiles';

// ---------------------------------------------------------------------------
// Mutation-key identities (D2.2, ARCFE-1053).
//
// These three are frozen here for a reason the GET keys did not have: each is
// SHARED by a pair of hooks the ownership ruling SPLIT. The first-person half
// moved into this package; the `*Member*` half stayed in the app hub because
// `/teams/{teamId}/members/{memberId}/patient-profiles**` is workspace's
// resource. Both halves must keep declaring the SAME mutation key — they are
// one logical in-flight identity — and "never re-declare a key literal in two
// places" (packages/CLAUDE.md) is what makes this the single site. The app hub
// imports them back from `@heidi/patients/contracts`.
//
// `'DeletePatientProfile'` is deliberately NOT here: its own/member pair use
// DIFFERENT literals (`'DeleteTeamMemberPatientProfile'`), so each is
// single-site already and the own literal moved inline with its hook.
// ---------------------------------------------------------------------------

export const UPDATE_PATIENT_PROFILE = 'UpdatePatientProfile';

export const LINK_SESSIONS_TO_PATIENT_PROFILE = 'LinkSessionsToPatientProfile';

export const UNLINK_SESSIONS_TO_PATIENT_PROFILE = 'UnlinkSessionsToPatientProfile';

// Verbatim from `src/hooks/query/useQueryPrechartInstructions.ts`, which now
// re-exports it. The write seeds this cache with `setQueryData` rather than
// invalidating, so the literal must stay byte-identical or the write is lost.
export const GET_PRECHART_INSTRUCTIONS = 'GetPrechartInstructions';
